/**
 * pageCache.js
 *
 * Cache singleton em memória para dados de página.
 * Padrão: stale-while-revalidate.
 *
 * Contrato:
 *   get(key) → data | null
 *   set(key, data) → void
 *   invalidate(key) → void
 *   clear() → void  ← chamado no logout para segurança
 *
 * TTL: 30 segundos. Após isso, get() retorna null e a página
 * busca normalmente, sem mostrar dado obsoleto.
 *
 * Segurança: clear() deve ser chamado no logout (AuthContext)
 * para evitar que dados de um usuário vazem para outro
 * no mesmo browser/computador.
 */

const TTL_MS = 30_000;

const _cache = new Map();

const pageCache = {
  get(key) {
    const entry = _cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > TTL_MS) {
      _cache.delete(key);
      return null;
    }
    return entry.data;
  },

  set(key, data) {
    _cache.set(key, { data, timestamp: Date.now() });
  },

  invalidate(key) {
    _cache.delete(key);
  },

  clear() {
    _cache.clear();
  },
};

export default pageCache;

/**
 * navState
 *
 * Persiste estado de navegação (filtros, página atual) entre sessões.
 * Usa localStorage com chave prefixada por seller_id para isolar usuários.
 *
 * Contrato:
 *   get(sellerId, tela, campo) → valor | null
 *   set(sellerId, tela, campo, valor) → void
 *   clear(sellerId) → void  ← chamado no logout junto com pageCache.clear()
 *
 * Formato da chave: dsc:nav:{sellerId}:{tela}:{campo}
 * Exemplo: dsc:nav:diogenes:cotacoes:page → 3
 *
 * Segurança: clear(sellerId) remove apenas as chaves do usuário atual.
 * Dados de outros usuários no mesmo browser não são afetados.
 */

const NAV_PREFIX = 'dsc:nav:';

export const navState = {
  _key(sellerId, tela, campo) {
    return `${NAV_PREFIX}${sellerId}:${tela}:${campo}`;
  },

  get(sellerId, tela, campo) {
    if (!sellerId) return null;
    try {
      const raw = localStorage.getItem(this._key(sellerId, tela, campo));
      if (raw === null) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },

  set(sellerId, tela, campo, valor) {
    if (!sellerId) return;
    try {
      localStorage.setItem(this._key(sellerId, tela, campo), JSON.stringify(valor));
    } catch {
      // localStorage indisponível (modo privado restrito) — degrada silenciosamente
    }
  },

  clear(sellerId) {
    if (!sellerId) return;
    const prefix = `${NAV_PREFIX}${sellerId}:`;
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) toRemove.push(key);
    }
    toRemove.forEach(key => localStorage.removeItem(key));
  },
};
